import { useState } from "react";
import { PriceAdjustmentOperation } from "../../Enums/PriceAdjustmentOperation";
import { Select } from "../Select/Select";
import { Input } from "../Input/Input";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";

type AdjustPricesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  categories: { id: string; name: string }[];
  onConfirm: (categoryId: string, percentage: number, operation: PriceAdjustmentOperation) => Promise<boolean>;
};

const AdjustPricesModal = ({ isOpen, onClose, categories, onConfirm }: AdjustPricesModalProps) => {
  const [operation, setOperation] = useState<PriceAdjustmentOperation>();
  const [categoryId, setCategoryId] = useState<string>("");
  const [percentage, setPercentage] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const resetFields = () => {
    setOperation(undefined);
    setCategoryId("");
    setPercentage(null);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!categoryId || percentage === null || operation === undefined) {
      toast.error("Selecione categoria, tipo de ajuste e insira uma porcentagem válida.");
      return;
    }

    setIsLoading(true);
    try {
      const updated = await onConfirm(categoryId, percentage, operation);
      if (updated) {
        toast.success("Preços atualizados com sucesso!");
        resetFields();
        onClose();
      } else {
        toast.error("Não foi possível atualizar os preços dos produtos.");
      }
    } catch {
      toast.error("Ocorreu um erro ao ajustar os preços. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-md shadow-xl px-6 py-8 relative w-full max-w-md">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-primary-600 hover:text-primary-700 text-2xl font-bold"
        >
          <FaTimes />
        </button>

        <h3 className="text-lg font-bold text-primary-600 mb-4">{"Ajustar preços por categoria"}</h3>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Select
            label={"Tipo de ajuste"}
            name="operation"
            value={operation ?? ""}
            onChange={(e) => {
              const value = Number(e.target.value) as PriceAdjustmentOperation;
              setOperation(value);
            }}
            options={[
              { label: "Aumentar preços", value: PriceAdjustmentOperation.Increase },
              { label: "Diminuir preços", value: PriceAdjustmentOperation.Decrease }
            ]}
          />

          <Input
            label={"Percentual de ajuste (%)"}
            name="percentage"
            type="number"
            value={percentage ?? ""}
            min={0}
            max={100}
            step={0.01}
            onChange={(e) => setPercentage(parseFloat(e.target.value))}
            required
          />

          <Select
            label={"Categoria"}
            name="categoryId"
            value={categoryId}
            placeholder={"Selecione uma categoria"}
            onChange={(e) => setCategoryId(e.target.value)}
            required
            options={categories.map(c => ({ value: c.id, label: c.name }))}
          />

          <Button isLoading={isLoading}
            type="submit"
            className="py-2"
            disabled={isLoading}
          >
            {"APLICAR ALTERAÇÃO"}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default AdjustPricesModal;
